(() => {
  "use strict";

  document.addEventListener("DOMContentLoaded", () => {
    // ==========================================================================
    // Toggle
    // ==========================================================================
    const hasGSAP = typeof window.gsap !== "undefined";

    // ------------------------------
    // パネル初期化（存在する場合のみ）
    // ------------------------------
    const panels = document.querySelectorAll(".js-form-toggle-content");
    if (panels.length > 0) {
      panels.forEach((panel, idx) => {
        // IDが無ければ自動採番（aria-controls用）
        if (!panel.id) panel.id = `formTogglePanel-${idx + 1}`;

        const hidden = panel.classList.contains("-hidden");
        if (hasGSAP) {
          gsap.set(panel, { autoAlpha: hidden ? 0 : 1 });
        } else {
          panel.style.visibility = hidden ? "hidden" : "visible";
          panel.style.opacity = hidden ? "0" : "1";
        }
        panel.setAttribute("tabindex", hidden ? "-1" : "0");
        panel.setAttribute("aria-hidden", hidden ? "true" : "false");
      });
    }

    // ------------------------------
    // ボタン初期化（ボタンだけあってもOK）
    // ------------------------------
    document.querySelectorAll(".js-form-toggle-button").forEach((btn) => {
      if (!btn.hasAttribute("type")) btn.setAttribute("type", "button");
      btn.setAttribute("aria-pressed", btn.classList.contains("-active") ? "true" : "false");
    });

    // ------------------------------
    // グルート要素の取得
    // ------------------------------
    const getGroupRoot = (el) => {
      const wrapped = el.closest(".js-form-toggle");
      if (wrapped) return wrapped;
      let cur = el;
      while (cur && cur !== document.body) {
        if (cur.querySelector(".js-form-toggle-content")) return cur;
        cur = cur.parentElement;
      }
      return document; // パネルが無い場合はdocumentでも安全に動作
    };

    // ------------------------------
    // 指定indexの表示切り替え
    // ------------------------------
    const showPanelAt = (root, index) => {
      const btns = Array.from(root.querySelectorAll(".js-form-toggle-button"));
      const panels = Array.from(root.querySelectorAll(".js-form-toggle-content"));

      // まずはボタンの状態だけ常に同期（パネルが無くても安全）
      btns.forEach((b, i) => {
        const isActive = i === index;
        b.classList.toggle("-active", isActive);
        b.setAttribute("aria-pressed", isActive ? "true" : "false");
        const panelId = panels[i]?.id;
        if (panelId) b.setAttribute("aria-controls", panelId);
        else b.removeAttribute("aria-controls");
      });

      // パネルが無ければここで終了（＝エラー回避）
      if (panels.length === 0) return;

      // indexに対応する要素が無い場合も安全に終了
      if (!btns[index] || !panels[index]) return;

      // 以降はパネルが存在する場合のみアニメーション
      panels.forEach((p, i) => {
        if (hasGSAP) gsap.killTweensOf(p);
        const isTarget = i === index;

        if (isTarget) {
          p.classList.remove("-hidden");
          p.removeAttribute("hidden");
          p.setAttribute("tabindex", "0");
          p.setAttribute("aria-hidden", "false");
          if (hasGSAP) {
            gsap.to(p, { duration: 0.5, autoAlpha: 1, ease: "power1.out" });
          } else {
            p.style.visibility = "visible";
            p.style.opacity = "1";
          }
        } else {
          p.setAttribute("tabindex", "-1");
          p.setAttribute("aria-hidden", "true");
          if (hasGSAP) {
            gsap.to(p, {
              duration: 0,
              autoAlpha: 0,
              ease: "power1.out",
              onComplete() {
                p.classList.add("-hidden");
                p.setAttribute("hidden", "");
              },
            });
          } else {
            p.style.visibility = "hidden";
            p.style.opacity = "0";
            p.classList.add("-hidden");
            p.setAttribute("hidden", "");
          }
        }
      });
    };

    // ------------------------------
    // 初期表示：-activeに揃える（パネル無しでも安全）
    // ------------------------------
    document.querySelectorAll(".js-form-toggle-button.-active").forEach((activeBtn) => {
      const root = getGroupRoot(activeBtn);
      const btns = Array.from(root.querySelectorAll(".js-form-toggle-button"));
      const index = btns.indexOf(activeBtn);
      if (index >= 0) showPanelAt(root, index);
    });

    // （任意）クリック時の切り替えも安全
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".js-form-toggle-button");
      if (!btn) return;
      const root = getGroupRoot(btn);
      const btns = Array.from(root.querySelectorAll(".js-form-toggle-button"));
      const idx = btns.indexOf(btn);
      if (idx >= 0) showPanelAt(root, idx);
    });

    // ==========================================================================
    // バリデーション
    // ==========================================================================
    // ===== ルール関数 =====
    const KATAKANA_RE = /^[\u30A0-\u30FFー・\s　]+$/;
    const isKatakana = (v) => KATAKANA_RE.test((v || "").trim());
    const isJpPhone = (v) => {
      const digits = String(v || "").replace(/\D/g, "");
      if (!/^0\d{9,10}$/.test(digits)) return false; // 0始まり + 桁数チェック
      return true;
    };

    function setupFormValidator(formId /* 例: 'js-form01' */) {
      const form = document.getElementById(formId);
      if (!form) return null; // フォームが無ければ何もしない

      const validator = new JustValidate(`#${formId}`, {
        focusInvalidField: true,
        lockForm: true,
        errorFieldCssClass: "is-invalid",
        successFieldCssClass: "is-valid",
        errorLabelCssClass: "error-msg",
        // インライン style は付けない（スタイルはクラスで管理）
        errorLabelStyle: {},
        errorFieldStyle: {},
        successFieldStyle: {},
      });

      // フィールド追加のヘルパー（このフォーム内にスコープ）
      const addFieldWithContainer = (fieldId, rules) => {
        const field = form.querySelector(`#${fieldId}`);
        if (!field) return;

        // 1) data-error-box="フィールドID" を最優先
        let container =
          form.querySelector(`.js-error[data-error-box="${field.id}"]`) ||
          // 2) [data-error-for="フィールドID"] 内の .js-error をフォールバック
          field.closest(`[data-error-for="${field.id}"]`)?.querySelector(".js-error") ||
          null;

        validator.addField(`#${fieldId}`, rules, container ? { errorsContainer: container } : undefined);
      };

      // ---------- このフォームのフィールドID（接頭辞は formId） ----------
      // 例: js-form01-preorderid / js-form02-preorderid / js-form03-preorderid
      const F = (name) => `${formId}-${name}`;

      // 必須系
      addFieldWithContainer(F("preorderid"), [{ rule: "required", errorMessage: "プレオーダーIDを入力してください" }]);

      addFieldWithContainer(F("lastname"), [{ rule: "required", errorMessage: "姓を入力してください" }]);

      addFieldWithContainer(F("firstname"), [{ rule: "required", errorMessage: "名を入力してください" }]);

      // カタカナ
      addFieldWithContainer(F("lastnamekana"), [
        { rule: "required", errorMessage: "姓をフリガナで入力してください" },
        { validator: isKatakana, errorMessage: "姓を全角カタカナで入力してください。" },
      ]);

      addFieldWithContainer(F("firstnamekana"), [
        { rule: "required", errorMessage: "名をフリガナで入力してください" },
        { validator: isKatakana, errorMessage: "名を全角カタカナで入力してください。" },
      ]);

      // メール
      addFieldWithContainer(F("email"), [
        { rule: "required", errorMessage: "メールアドレスを入力してください" },
        { rule: "email", errorMessage: "メール形式が正しくありません。" },
      ]);

      // 電話番号
      addFieldWithContainer(F("tel"), [
        { rule: "required", errorMessage: "電話番号を入力してください" },
        { validator: isJpPhone, errorMessage: "0から始まる10〜11桁で入力してください（ハイフン可）。" },
      ]);

      // お問い合わせ本文
      addFieldWithContainer(F("msg"), [{ rule: "required", errorMessage: "お問い合わせ内容を入力してください" }]);

      // ライブ再検証（存在する要素のみ）
      [F("preorderid"), F("lastname"), F("firstname"), F("lastnamekana"), F("firstnamekana"), F("email"), F("tel")].forEach((id) => {
        const el = form.querySelector(`#${id}`);
        if (el) el.addEventListener("input", () => validator.revalidateField(`#${id}`));
      });

      validator.onSuccess((e) => {
        e.target.submit();
      });

      return validator;
    }

    // ====== 初期化呼び出し ======
    setupFormValidator("js-form01");
    setupFormValidator("js-form02");
    setupFormValidator("js-form03");

    // ==========================================================================
    // textarea
    // ==========================================================================
    document.querySelectorAll(".js-textarea-wrap").forEach(flexTextarea);
    function flexTextarea(el) {
      const dummy = el.querySelector(".js-textarea-dummy");
      el.querySelector(".js-textarea").addEventListener("input", (e) => {
        dummy.textContent = e.target.value + "\u200b";
      });
    }
  });
})();
